import { ReactNode } from 'react';

import { IconAiAgent } from '@components/icons/ai-agent';
import { IconList } from '@components/icons/list';
import { IconTraveller } from '@components/icons/traveller';
import { getScopedTranslate } from '@locale/translate';

import { usePermissions } from 'contexts/permissions-context';
import { OAuthCallbackPage } from 'ui/unauthenticated/pages/oauth-callback';
import { JourneyTemplate } from 'ui/unauthenticated/pages/templates/journey-template';

import { AIAgentList } from './pages/ai-agents/list';
import { CompanyTravellerList } from './pages/company-travellers/list';
import { JourneyDetails } from './pages/journeys/journey-details';
import { JourneyEdit } from './pages/journeys/journey-edit';
import { JourneyList } from './pages/journeys/journey-list';
import { TravellerPage } from './pages/travellers/traveller-page';

import { AccessDenied } from '../access-denied';
import { DocumentDetail } from '../settings/pages/documents/detail';
import { DocumentsList } from '../settings/pages/documents/list';
import { CompanyLists } from '../settings/pages/organization/lists/list';

const locale = {
  en: {
    lists: 'Lists',
    travellerProfiles: 'Traveller profiles',
    aiAgentTemplates: 'AI Agent templates',
    journeys: 'Journeys',
    journeyBuilder: 'Journey Builder',
    journey: 'Journey',
    traveller: 'Traveller',
    documents: 'Documents',
    document: 'Document',
    template: 'Template',
  },
};

const { t } = getScopedTranslate(locale);

const AdminOrEditorGuard = ({ children }: { children: ReactNode }) => {
  const { canViewTravellerProfiles } = usePermissions();

  if (!canViewTravellerProfiles) {
    return <AccessDenied />;
  }
  return <>{children}</>;
};

export const mainRoutes = [
  {
    pageTitle: t('journeys'),
    name: 'workspaceJourneyList',
    path: 'workspaces/:workspaceId/journeys',
    page: <JourneyList />,
  },
  {
    commandPaletteName: t('travellerProfiles'),
    commandPaletteIcon: IconTraveller,
    name: 'travellersList',
    path: 'travellers/*',
    page: (
      <AdminOrEditorGuard>
        <CompanyTravellerList />
      </AdminOrEditorGuard>
    ),
  },
  {
    pageTitle: t('journeyBuilder'),
    name: 'editJourney',
    path: 'workspaces/:workspaceId/journeys/:journeyId/edit/*',
    page: <JourneyEdit />,
    hideNavbar: true,
  },
  {
    pageTitle: t('journey'),
    name: 'journeyDetails',
    path: 'workspaces/:workspaceId/journeys/:journeyId/*',
    page: <JourneyDetails />,
  },
  {
    pageTitle: t('traveller'),
    name: 'traveller',
    path: 'workspaces/:workspaceId/journeys/:journeyId/travellers/:travellerId/*',
    page: <TravellerPage />,
  },
  {
    commandPaletteName: t('lists'),
    commandPaletteIcon: IconList,
    name: 'companyLists',
    path: 'lists',
    page: (
      <AdminOrEditorGuard>
        <CompanyLists />
      </AdminOrEditorGuard>
    ),
  },
  {
    commandPaletteName: t('aiAgentTemplates'),
    commandPaletteIcon: IconAiAgent,
    name: 'aiAgents',
    path: 'ai-agents',
    page: (
      <AdminOrEditorGuard>
        <AIAgentList />
      </AdminOrEditorGuard>
    ),
  },
  {
    pageTitle: t('documents'),
    name: 'workspaceDocuments',
    path: 'workspaces/:workspaceId/documents',
    page: <DocumentsList />,
  },
  {
    pageTitle: t('document'),
    name: 'worksspaceDocumentDetail',
    path: 'workspaces/:workspaceId/documents/:documentId/*',
    page: <DocumentDetail />,
  },
  {
    pageTitle: t('template'),
    name: 'templates',
    path: 'templates/:journeyId/',
    page: <JourneyTemplate />,
    hideNavbar: true,
  },
  {
    name: 'oauthCallback',
    path: 'oauth/callback',
    page: <OAuthCallbackPage />,
    hideNavbar: true,
  },
] as const;
